import React, { useMemo } from 'react';
import moment from 'moment';
import { DatePicker } from '@packages/ui/composites/DatePicker';
import { DATE_FORMAT } from '@packages/shared/constants';
import type { FilterDef } from '../../schema/types';
import { TABLE_IDS } from '../../semantic-contract';

export function formatFilterDateDisplay(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') return '';
  const m = moment(String(raw));
  return m.isValid() ? m.format(DATE_FORMAT) : String(raw);
}

function parseToDate(raw: unknown): Date | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const m = moment(String(raw));
  return m.isValid() ? m.toDate() : null;
}

interface DateFilterProps {
  filter: FilterDef;
  value: unknown;
  onFilterChange: (name: string, value: unknown) => void;
  pendingFilter: Record<string, unknown>;
}

export function DateFilter({
  filter,
  value,
  onFilterChange,
  pendingFilter,
}: DateFilterProps): JSX.Element {
  const dependency = filter.dependsOn;
  const depMet = dependency ? Boolean(pendingFilter[dependency]) : true;

  const dateValue = useMemo(() => parseToDate(value), [value]);
  const minDate = useMemo(
    () => (filter.minDate ? moment(filter.minDate).toDate() : undefined),
    [filter.minDate],
  );
  const maxDate = useMemo(
    () => (filter.maxDate ? moment(filter.maxDate).toDate() : undefined),
    [filter.maxDate],
  );

  return (
    <DatePicker
      name={filter.name}
      placeholder={filter.placeholder ?? DATE_FORMAT}
      value={dateValue}
      onChange={(date: Date | null) => {
        if (date) {
          onFilterChange(filter.name, moment(date).format(DATE_FORMAT));
        } else {
          onFilterChange(filter.name, undefined);
        }
      }}
      disabled={!depMet}
      minDate={minDate}
      maxDate={maxDate}
      portalToBody
      calendarDensity='production'
      semanticId={TABLE_IDS.filterDate(filter.name)}
    />
  );
}
